import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, fmt } from "@/components/shared";
import { CheckSquare, Film, TrendingUp, Flag, ArrowRight, Loader2, Trophy, Eye, Heart } from "lucide-react";

const platformColors = { youtube: "#FF0000", tiktok: "#FF0050", instagram: "#E1306C" };
const ratio = (v) => (v?.views ? Math.round((v.likes / v.views) * 10000) / 100 : 0);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [videos, setVideos] = useState([]);
  const [lbTimeframe, setLbTimeframe] = useState("all");

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/marketing/summary?platform=all"),
      api.get("/calendar"),
      api.get("/videos"),
    ]).then(([s, m, c, v]) => {
      setStats(s.data);
      setSummary(m.data);
      setCalendar(c.data);
      setVideos(v.data);
    });
  }, []);

  if (!stats) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;

  const cards = [
    { label: "Open Tasks", value: stats.open_tasks, sub: `${stats.total_tasks} total`, icon: CheckSquare, to: "/tasks", color: "text-cyan-400" },
    { label: "Videos", value: stats.total_videos, sub: "in library", icon: Film, to: "/videos", color: "text-violet-400" },
    { label: "Total Followers", value: fmt(summary?.totals?.followers || 0), sub: "across platforms", icon: TrendingUp, to: "/marketing", color: "text-primary" },
    { label: "Milestones", value: stats.upcoming_milestones, sub: "upcoming", icon: Flag, to: "/calendar", color: "text-red-400" },
  ];

  const upcoming = [...calendar]
    .filter((e) => new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 6);

  const lbStart = lbTimeframe === "week" ? new Date(Date.now() - 7 * 864e5) : lbTimeframe === "month" ? new Date(Date.now() - 30 * 864e5) : null;
  const refDate = (v) => new Date(v.release_date || v.created_at);
  const leaderboard = [...videos]
    .filter((v) => (v.views || 0) > 0)
    .filter((v) => !lbStart || refDate(v) >= lbStart)
    .sort((a, b) => ratio(b) - ratio(a))
    .slice(0, 5);

  const eventLink = (e) => e.type === "milestone" ? "/calendar" : `/${e.type === "video" ? "videos" : "tasks"}?open=${e.id}`;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Studio overview at a glance." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}
            className="group glass border border-border rounded-2xl p-5 hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-3">
              <c.icon className={`w-5 h-5 ${c.color}`} />
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="font-display text-3xl font-bold">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.label} · {c.sub}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass border border-border rounded-2xl p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Platform Snapshot</h3>
          <div className="space-y-3">
            {summary && Object.entries(summary.platforms).map(([p, d]) => (
              <div key={p} className="flex items-center gap-4 p-3 rounded-xl bg-secondary/40">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: platformColors[p] }} />
                <span className="capitalize font-medium w-24">{p}</span>
                <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                  <div><span className="text-muted-foreground text-xs block">Followers</span><span className="font-mono">{fmt(d.followers)}</span></div>
                  <div><span className="text-muted-foreground text-xs block">Views</span><span className="font-mono">{fmt(d.views)}</span></div>
                  <div><span className="text-muted-foreground text-xs block">Likes/View</span><span className="font-mono text-primary">{d.likes_view_ratio}%</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass border border-border rounded-2xl p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Upcoming</h3>
          <div className="space-y-2">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">Nothing scheduled.</p>}
            {upcoming.map((e) => (
              <Link key={`${e.type}-${e.id}`} to={eventLink(e)}
                className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/60 transition-colors group">
                <span className={`w-1.5 h-8 rounded-full ${e.type === "video" ? "bg-cyan-400" : e.type === "task" ? "bg-violet-400" : "bg-primary"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{e.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{e.type} · {new Date(e.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="glass border border-border rounded-2xl p-6 mt-6" data-testid="performance-leaderboard">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h3 className="font-display font-semibold text-lg">Top Performing Videos</h3>
            <span className="text-xs text-muted-foreground ml-1 hidden sm:inline">by likes / view ratio</span>
          </div>
          <div className="flex gap-1 bg-secondary/50 p-1 rounded-lg">
            {[{ v: "week", l: "This Week" }, { v: "month", l: "This Month" }, { v: "all", l: "All Time" }].map((t) => (
              <button key={t.v} onClick={() => setLbTimeframe(t.v)} data-testid={`leaderboard-tf-${t.v}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${lbTimeframe === t.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No video metrics yet. Add views &amp; likes on your videos to rank them here.</p>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((v, i) => (
              <Link key={v.id} to={`/videos?open=${v.id}`} data-testid={`leaderboard-row-${i}`}
                className="flex items-center gap-4 p-3 rounded-xl bg-secondary/40 hover:bg-secondary/70 hover:border-primary/40 border border-transparent transition-all group">
                <span className={`font-display font-bold text-lg w-6 text-center ${i === 0 ? "text-primary" : "text-muted-foreground"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{v.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{fmt(v.views || 0)}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{fmt(v.likes || 0)}</span>
                  </p>
                </div>
                <span className="font-mono text-primary font-semibold text-sm">{ratio(v)}%</span>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
